import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
  Divider,
} from '@mui/material';
import FormActionBar from '../../../shared/components/FormActionBar';
import ScopePicker from './ScopePicker';
import type { Portfolio, ServiceComponentSummary } from '../services/catalogService';
import type { ServiceComponentFormData } from '../hooks/useServiceComponents';

interface ServiceComponentEditFormProps {
  formData: ServiceComponentFormData;
  fieldErrors: Record<string, string | null>;
  portfolios: Portfolio[];
  showRenameWarning: boolean;
  originalName: string | null;
  dependsOnOptions: string[];
  saving: boolean;
  selectedEntity: ServiceComponentSummary | null;
  hasChanges: () => boolean;
  handleChange: <K extends keyof ServiceComponentFormData>(field: K, value: ServiceComponentFormData[K]) => void;
  handleSave: () => void;
  handleDeleteClick: () => void;
}

const ServiceComponentEditForm: React.FC<ServiceComponentEditFormProps> = ({
  formData,
  fieldErrors,
  portfolios,
  showRenameWarning,
  originalName,
  dependsOnOptions,
  saving,
  selectedEntity,
  hasChanges,
  handleChange,
  handleSave,
  handleDeleteClick,
}) => {
  const portfolioDisplay = portfolios.find((p) => p.name === formData.portfolio);

  return (
  <>
    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>ServiceComponent Details</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
      <TextField
        label="Name"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        error={!!fieldErrors.name}
        helperText={fieldErrors.name ?? undefined}
        fullWidth
        size="small"
      />
      {showRenameWarning && (
        <Typography variant="body2" color="warning.main">
          Other components in this service depend on <strong>{originalName}</strong>. Renaming will break their dependency references.
        </Typography>
      )}

      <TextField
        label="Description"
        value={formData.description}
        onChange={(e) => handleChange('description', e.target.value)}
        fullWidth
        multiline
        rows={2}
        size="small"
      />

      <TextField
        label="Portfolio"
        value={portfolioDisplay?.display_name || formData.portfolio}
        fullWidth
        size="small"
        disabled
      />

      <ScopePicker
        value={formData.scope}
        onChange={(value) => handleChange('scope', value)}
      />

      <Autocomplete
        multiple
        options={dependsOnOptions}
        value={formData.depends_on}
        onChange={(_, value) => handleChange('depends_on', value)}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => {
            const tagProps = getTagProps({ index });
            return <Chip {...tagProps} label={tag} size="small" />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Depends On"
            size="small"
            placeholder="Select components this depends on"
            helperText={fieldErrors.depends_on || "Deployment is blocked until all dependencies are IN_SYNC"}
            error={!!fieldErrors.depends_on}
          />
        )}
      />

      <Divider />

      <FormControlLabel
        control={
          <Switch
            checked={formData.auto_deploy}
            onChange={(e) => handleChange('auto_deploy', e.target.checked)}
            size="small"
          />
        }
        label={
          <Box>
            <Typography variant="body2">Auto Deploy</Typography>
            <Typography variant="caption" color="text.secondary">
              Deploy automatically on customer subscribe; disable for opt-in components
            </Typography>
          </Box>
        }
      />

      <TextField
        label="Source Repository"
        value={formData.source_repository}
        onChange={(e) => handleChange('source_repository', e.target.value)}
        fullWidth
        size="small"
        placeholder="https://github.com/..."
      />


    </Box>

    <FormActionBar
      onSave={handleSave}
      saving={saving}
      saveDisabled={saving || !hasChanges()}
      onDelete={selectedEntity ? handleDeleteClick : undefined}
    />
  </>
  );
};

export default ServiceComponentEditForm;
